import { Link, useParams } from "react-router-dom";
import * as Lucide from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumbs, Faq } from "@/components/Shared";
import { BRAND, SERVICES } from "@/data/site";
import {
  FR_CITIES, FR_SERVICES, FR_FAQS, FR_STEPS, frUrl,
  frServiceCityContent, frCityContent, frProvContent, frServiceContent, frServiceProvContent,
  metaFrHub, metaFrProv, metaFrCity, metaFrService, metaFrServiceProv, metaFrServiceCity,
} from "@/data/fr";
import { url, provBySlug, cityBySlug, serviceBySlug, breadcrumbLd, faqLd, serviceLd } from "@/data/content";
import NotFound from "@/pages/NotFound";

const Icon = ({ name, className }) => {
  const C = Lucide[name] || Lucide.Shield;
  return <C className={className} />;
};

const QC = { slug: "qc", name: "Quebec", abbr: "QC" };

const alts = (en, fr) => [
  { hreflang: "en", path: en },
  { hreflang: "fr", path: fr },
  { hreflang: "x-default", path: en },
];

const LangToggle = ({ enPath }) => (
  <Link to={enPath} data-testid="lang-toggle-en" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-4 py-2 text-xs font-semibold text-slate-300 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
    <Lucide.Languages className="h-3.5 w-3.5" /> View this page in English
  </Link>
);

const FrCta = ({ heading = "Obtenez une soumission le jour meme", sub }) => (
  <section data-testid="fr-cta-band" className="relative overflow-hidden rounded-2xl border border-amber-500/25 bg-gradient-to-br from-slate-900 via-[#111827] to-slate-900 p-8 sm:p-12 my-14">
    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 85% 10%, rgba(245,158,11,0.16) 0%, transparent 55%)" }} />
    <div className="relative flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
      <div className="max-w-xl">
        <div className="flex items-center gap-2 text-amber-500 font-mono text-xs uppercase tracking-[0.25em] mb-3">
          <Lucide.Radio className="h-3.5 w-3.5 animate-pulse" /> Repartition 24/7/365
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight">{heading}</h2>
        <p className="mt-3 text-slate-400 text-sm sm:text-base leading-relaxed">
          {sub || "Parlez directement a notre centrale. Aucun formulaire: une soumission ecrite et detaillee le jour meme, partout au Quebec et au Canada."}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0">
        <a href={BRAND.phoneHref} data-testid="fr-cta-call" className="inline-flex items-center justify-center gap-2.5 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-slate-950 shadow-lg shadow-amber-500/25 transition-all hover:bg-amber-400 hover:-translate-y-0.5">
          <Lucide.Phone className="h-4 w-4" /> {BRAND.phone}
        </a>
        <a href={BRAND.emailHref} data-testid="fr-cta-email" className="inline-flex items-center justify-center gap-2.5 rounded-full border border-slate-600 bg-slate-800/60 px-7 py-3.5 text-sm font-semibold text-slate-100 transition-all hover:border-amber-500/60 hover:text-amber-400 hover:-translate-y-0.5">
          <Lucide.Mail className="h-4 w-4" /> {BRAND.email}
        </a>
      </div>
    </div>
  </section>
);

const FrHero = ({ crumbs, eyebrow, h1, testid, enPath }) => (
  <section className="relative overflow-hidden border-b border-white/5">
    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(245,158,11,0.12) 0%, transparent 65%)" }} />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 relative">
      <Breadcrumbs items={crumbs} />
      <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] text-amber-500 mb-4">{eyebrow}</span>
      <h1 data-testid={testid} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-display tracking-tight text-slate-50 max-w-4xl">{h1}</h1>
      <div className="mt-6 flex flex-wrap gap-3">
        <a href={BRAND.phoneHref} data-testid="fr-hero-call" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-slate-950 hover:bg-amber-400 transition-colors">
          <Lucide.Phone className="h-4 w-4" /> {BRAND.phone}
        </a>
        <a href={BRAND.emailHref} data-testid="fr-hero-email" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-100 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
          <Lucide.Mail className="h-4 w-4" /> {BRAND.email}
        </a>
        <LangToggle enPath={enPath} />
      </div>
    </div>
  </section>
);

const FrFeatures = ({ s }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 my-10">
    {s.features.map((f) => (
      <div key={f.t} className="rounded-xl border border-slate-800 bg-[#111827] p-6">
        <Lucide.CheckCircle2 className="h-5 w-5 text-amber-400 mb-3" />
        <h3 className="font-semibold text-slate-100 mb-1.5 text-sm sm:text-base">{f.t}</h3>
        <p className="text-sm text-slate-400 leading-relaxed">{f.d}</p>
      </div>
    ))}
  </div>
);

const FrSteps = () => (
  <section className="my-14">
    <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mb-8">Comment fonctionne le deploiement</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {FR_STEPS.map((st, i) => (
        <div key={st.t} className="rounded-xl border border-slate-800 bg-[#111827] p-6">
          <span className="font-mono text-3xl font-bold text-amber-500/30">0{i + 1}</span>
          <h3 className="font-semibold text-slate-100 mt-2 mb-2 text-sm sm:text-base">{st.t}</h3>
          <p className="text-sm text-slate-400 leading-relaxed">{st.d}</p>
        </div>
      ))}
    </div>
  </section>
);

const FrLinkGrid = ({ title, links, testid }) => (
  <section className="my-14" data-testid={testid}>
    <h2 className="text-xl sm:text-2xl font-bold font-display text-slate-50 tracking-tight mb-5">{title}</h2>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
      {links.map((l) => (
        <Link key={l.path} to={l.path} className="rounded-lg border border-slate-800 bg-[#111827] px-4 py-3 text-sm text-slate-300 transition-all hover:border-amber-500/50 hover:text-amber-400 hover:-translate-y-0.5">
          {l.label}
        </Link>
      ))}
    </div>
  </section>
);

export function FrHub() {
  const meta = metaFrHub();
  return (
    <div data-testid="fr-hub-page">
      <Seo title={meta.title} description={meta.description} path="/fr/" lang="fr" alternates={alts("/", "/fr/")} jsonLd={[breadcrumbLd([{ name: "Accueil", path: "/" }, { name: "Quebec", path: "/fr/" }])]} />
      <FrHero crumbs={[{ name: "Accueil", path: "/" }, { name: "Quebec", path: "/fr/" }]} eyebrow="Entreprise autochtone — Gardiens licencies BSP" h1="Services de gardiens de securite au Quebec" testid="fr-hub-h1" enPath="/" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="max-w-3xl space-y-5 text-slate-300 text-sm sm:text-base leading-relaxed mb-12">
          <p className="text-base sm:text-lg text-slate-200">{BRAND.name} est une entreprise de securite appartenant a des Premieres Nations, desservant le Quebec avec des gardiens licencies par le Bureau de la securite privee (BSP), assures et supervises.</p>
          <p>De Montreal a Quebec, de Gatineau a Saguenay, nos equipes protegent chantiers, commerces, hopitaux, evenements et installations industrielles. Repartition 24/7 au {BRAND.phone}, soumissions ecrites le jour meme.</p>
        </div>
        <FrLinkGrid testid="fr-hub-cities" title="Nos villes desservies au Quebec" links={FR_CITIES.map((c) => ({ path: frUrl.city(c), label: `Gardiens de securite ${c.name}` }))} />
        <h2 className="text-xl sm:text-2xl font-bold font-display text-slate-50 tracking-tight mb-5">Nos services en francais</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((sEn) => {
            const s = FR_SERVICES[sEn.slug];
            return (
              <Link key={sEn.slug} to={frUrl.service(sEn)} data-testid={`fr-service-card-${sEn.slug}`} className="group rounded-2xl border border-slate-800 bg-[#111827] p-6 transition-all hover:border-amber-500/50 hover:-translate-y-1">
                <Icon name={sEn.icon} className="h-6 w-6 text-amber-400 mb-3" />
                <h3 className="font-display font-bold text-slate-100 mb-2 group-hover:text-amber-400 transition-colors">{s.name}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.short}</p>
              </Link>
            );
          })}
        </div>
        <FrCta />
      </div>
    </div>
  );
}

export function FrProvince() {
  const meta = metaFrProv();
  const pc = frProvContent();
  const crumbs = [{ name: "Accueil", path: "/fr/" }, { name: "Quebec", path: "/fr/quebec/" }];
  const faqs = FR_FAQS.map((f) => ({ q: f.q.replaceAll("{area}", "votre region"), a: f.a.replaceAll("{area}", "votre region") }));
  return (
    <div data-testid="fr-province-page">
      <Seo title={meta.title} description={meta.description} path="/fr/quebec/" lang="fr" alternates={alts("/locations/qc/", "/fr/quebec/")} jsonLd={[faqLd(faqs), breadcrumbLd(crumbs)]} />
      <FrHero crumbs={crumbs} eyebrow="Gardiens licencies BSP — Couverture provinciale 24/7" h1={pc.h1} testid="fr-province-h1" enPath="/locations/qc/" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="max-w-3xl space-y-5 text-slate-300 text-sm sm:text-base leading-relaxed mb-12">
          {pc.intro.map((p, i) => <p key={i} className={i === 0 ? "text-base sm:text-lg text-slate-200" : ""}>{p}</p>)}
        </div>
        <FrLinkGrid testid="fr-province-cities" title="Villes desservies au Quebec" links={FR_CITIES.map((c) => ({ path: frUrl.city(c), label: `Gardiens de securite ${c.name}` }))} />
        <FrLinkGrid testid="fr-province-services" title="Services offerts partout au Quebec" links={SERVICES.map((sEn) => ({ path: frUrl.serviceProv(sEn), label: `${FR_SERVICES[sEn.slug].name} Quebec` }))} />
        <FrSteps />
        <Faq items={faqs} heading="Questions frequentes — Securite au Quebec" />
        <FrCta />
      </div>
    </div>
  );
}

export function FrCity() {
  const { citySlug } = useParams();
  const c = FR_CITIES.find((x) => x.slug === citySlug);
  if (!c) return <NotFound />;
  const enCity = cityBySlug(c.slug);
  const enPath = url.city(enCity);
  const meta = metaFrCity(c);
  const cc = frCityContent(c);
  const crumbs = [{ name: "Accueil", path: "/fr/" }, { name: "Quebec", path: "/fr/quebec/" }, { name: c.name, path: frUrl.city(c) }];
  return (
    <div data-testid={`fr-city-page-${c.slug}`}>
      <Seo title={meta.title} description={meta.description} path={frUrl.city(c)} lang="fr" alternates={alts(enPath, frUrl.city(c))} jsonLd={[faqLd(cc.faqs), breadcrumbLd(crumbs)]} />
      <FrHero crumbs={crumbs} eyebrow={`Agence de securite 24/7 — ${c.name}, QC`} h1={cc.h1} testid="fr-city-h1" enPath={enPath} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="max-w-3xl space-y-5 text-slate-300 text-sm sm:text-base leading-relaxed">
          {cc.intro.map((p, i) => <p key={i} className={i === 0 ? "text-base sm:text-lg text-slate-200" : ""}>{p}</p>)}
        </div>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          <div className="rounded-xl border border-slate-800 bg-[#111827] p-6">
            <h2 className="font-display font-bold text-slate-50 mb-4 flex items-center gap-2"><Lucide.Landmark className="h-4 w-4 text-amber-400" /> Secteurs couverts a {c.name}</h2>
            <ul className="space-y-2.5">
              {c.landmarks.map((l) => (
                <li key={l} className="flex items-start gap-2 text-sm text-slate-400"><Lucide.CheckCircle2 className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" /> {l.charAt(0).toUpperCase() + l.slice(1)}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-slate-800 bg-[#111827] p-6">
            <h2 className="font-display font-bold text-slate-50 mb-4 flex items-center gap-2"><Lucide.Briefcase className="h-4 w-4 text-amber-400" /> Industries protegees a {c.name}</h2>
            <ul className="space-y-2.5">
              {c.industries.map((ind) => (
                <li key={ind} className="flex items-start gap-2 text-sm text-slate-400"><Lucide.CheckCircle2 className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" /> {ind.charAt(0).toUpperCase() + ind.slice(1)}</li>
              ))}
            </ul>
          </div>
        </div>
        <FrLinkGrid
          testid="fr-city-services"
          title={`Tous nos services a ${c.name}`}
          links={SERVICES.map((sEn) => ({ path: frUrl.serviceCity(sEn, c), label: `${FR_SERVICES[sEn.slug].name} ${c.name}` }))}
        />
        <Faq items={cc.faqs} heading={`Gardiens de securite a ${c.name} — Questions frequentes`} />
        <FrCta heading={`Embauchez des gardiens de securite a ${c.name} aujourd'hui`} sub={`Agents licencies et assures dans ${c.metro}. Appelez le ${BRAND.phone} ou ecrivez a ${BRAND.email} pour une soumission le jour meme.`} />
      </div>
    </div>
  );
}

export function FrService() {
  const { serviceSlug } = useParams();
  const sEn = serviceBySlug(serviceSlug);
  if (!sEn || !FR_SERVICES[sEn.slug]) return <NotFound />;
  const s = FR_SERVICES[sEn.slug];
  const meta = metaFrService(sEn);
  const sc = frServiceContent(sEn);
  const crumbs = [{ name: "Accueil", path: "/fr/" }, { name: "Services", path: "/fr/" }, { name: s.name, path: frUrl.service(sEn) }];
  const faqs = FR_FAQS.map((f) => ({ q: f.q.replaceAll("{area}", "votre region"), a: f.a.replaceAll("{area}", "votre region") }));
  return (
    <div data-testid={`fr-service-page-${sEn.slug}`}>
      <Seo title={meta.title} description={meta.description} path={frUrl.service(sEn)} lang="fr" alternates={alts(url.service(sEn), frUrl.service(sEn))} jsonLd={[serviceLd(sEn), faqLd(faqs), breadcrumbLd(crumbs)]} />
      <FrHero crumbs={crumbs} eyebrow="Service disponible partout au Canada — 24/7" h1={sc.h1} testid="fr-service-h1" enPath={url.service(sEn)} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="max-w-3xl space-y-5 text-slate-300 text-sm sm:text-base leading-relaxed">
          {sc.intro.map((p, i) => <p key={i} className={i === 0 ? "text-base sm:text-lg text-slate-200" : ""}>{p}</p>)}
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mt-12">Ce que comprend le service</h2>
        <FrFeatures s={s} />
        <FrLinkGrid testid="fr-service-cities" title={`${s.name} par ville au Quebec`} links={FR_CITIES.map((c) => ({ path: frUrl.serviceCity(sEn, c), label: `${s.name} ${c.name}` }))} />
        <Faq items={faqs} heading={`${s.name} — Questions frequentes`} />
        <FrCta heading={`Obtenez le service ${s.name.toLowerCase()} partout au Canada`} />
      </div>
    </div>
  );
}

export function FrServiceProv() {
  const { serviceSlug } = useParams();
  const sEn = serviceBySlug(serviceSlug);
  if (!sEn || !FR_SERVICES[sEn.slug]) return <NotFound />;
  const s = FR_SERVICES[sEn.slug];
  const meta = metaFrServiceProv(sEn);
  const sc = frServiceProvContent(sEn);
  const crumbs = [{ name: "Accueil", path: "/fr/" }, { name: "Services", path: "/fr/" }, { name: s.name, path: frUrl.service(sEn) }, { name: "Quebec", path: frUrl.serviceProv(sEn) }];
  const faqs = FR_FAQS.map((f) => ({ q: f.q.replaceAll("{area}", "votre region"), a: f.a.replaceAll("{area}", "votre region") }));
  return (
    <div data-testid={`fr-service-prov-page-${sEn.slug}`}>
      <Seo title={meta.title} description={meta.description} path={frUrl.serviceProv(sEn)} lang="fr" alternates={alts(url.serviceProv(sEn, provBySlug("qc")), frUrl.serviceProv(sEn))} jsonLd={[serviceLd(sEn, "Quebec"), faqLd(faqs), breadcrumbLd(crumbs)]} />
      <FrHero crumbs={crumbs} eyebrow="Gardiens licencies BSP — Couverture provinciale 24/7" h1={sc.h1} testid="fr-service-prov-h1" enPath={url.serviceProv(sEn, provBySlug("qc"))} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="max-w-3xl space-y-5 text-slate-300 text-sm sm:text-base leading-relaxed">
          {sc.intro.map((p, i) => <p key={i} className={i === 0 ? "text-base sm:text-lg text-slate-200" : ""}>{p}</p>)}
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mt-12">Ce que comprend le service au Quebec</h2>
        <FrFeatures s={s} />
        <FrLinkGrid testid="fr-service-prov-cities" title={`${s.name} par ville au Quebec`} links={FR_CITIES.map((c) => ({ path: frUrl.serviceCity(sEn, c), label: `${s.name} ${c.name}` }))} />
        <FrLinkGrid testid="fr-service-prov-others" title="Autres services offerts au Quebec" links={SERVICES.filter((x) => x.slug !== sEn.slug).map((x) => ({ path: frUrl.serviceProv(x), label: `${FR_SERVICES[x.slug].name} Quebec` }))} />
        <FrSteps />
        <Faq items={faqs} heading={`${s.name} au Quebec — Questions frequentes`} />
        <FrCta heading={`Embauchez le service ${s.name.toLowerCase()} partout au Quebec`} />
      </div>
    </div>
  );
}

export function FrServiceCity() {
  const { serviceSlug, citySlug } = useParams();
  const sEn = serviceBySlug(serviceSlug);
  const c = FR_CITIES.find((x) => x.slug === citySlug);
  if (!sEn || !FR_SERVICES[sEn.slug] || !c) return <NotFound />;
  const s = FR_SERVICES[sEn.slug];
  const meta = metaFrServiceCity(sEn, c);
  const cc = frServiceCityContent(sEn, c);
  const enPath = url.serviceCity(sEn, cityBySlug(c.slug));
  const crumbs = [{ name: "Accueil", path: "/fr/" }, { name: "Services", path: "/fr/" }, { name: s.name, path: frUrl.service(sEn) }, { name: c.name, path: frUrl.serviceCity(sEn, c) }];
  const siblings = FR_CITIES.filter((x) => x.slug !== c.slug);
  return (
    <div data-testid={`fr-service-city-page-${sEn.slug}-${c.slug}`}>
      <Seo title={meta.title} description={meta.description} path={frUrl.serviceCity(sEn, c)} lang="fr" alternates={alts(enPath, frUrl.serviceCity(sEn, c))} jsonLd={[serviceLd(sEn, `${c.name}, QC`), faqLd(cc.faqs), breadcrumbLd(crumbs)]} />
      <FrHero crumbs={crumbs} eyebrow={`Licencie • Assure • Repartition 24/7 a ${c.name}`} h1={cc.h1} testid="fr-service-city-h1" enPath={enPath} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="space-y-5 text-slate-300 text-sm sm:text-base leading-relaxed">
              {cc.intro.map((p, i) => <p key={i} className={i === 0 ? "text-base sm:text-lg text-slate-200" : ""}>{p}</p>)}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mt-12">Ce que comprend le service a {c.name}</h2>
            <FrFeatures s={s} />
            <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mt-4">{cc.localH}</h2>
            <div className="space-y-5 mt-5 text-slate-300 text-sm sm:text-base leading-relaxed">
              {cc.localPs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mt-12">Pourquoi {c.name} choisit {BRAND.name}</h2>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {cc.why.map((w) => (
                <li key={w} className="flex items-start gap-3 rounded-xl border border-slate-800 bg-[#111827] px-4 py-3.5 text-sm text-slate-300">
                  <Lucide.CheckCircle2 className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" /> {w}
                </li>
              ))}
            </ul>
            <p className="mt-8 text-slate-300 text-sm sm:text-base leading-relaxed">{cc.closing}</p>
            <FrSteps />
            <Faq items={cc.faqs} heading={`${s.name} a ${c.name} — Questions frequentes`} />
          </div>
          <aside>
            <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-b from-[#111827] to-slate-900 p-6 sticky top-28">
              <Icon name={sEn.icon} className="h-8 w-8 text-amber-400 mb-4" />
              <h3 className="font-display font-bold text-slate-50 mb-2">{s.name} — {c.name}</h3>
              <p className="text-xs text-slate-400 leading-relaxed mb-5">Soumissions ecrites le jour meme. Deploiement d'urgence disponible ce soir dans {c.metro}.</p>
              <a href={BRAND.phoneHref} data-testid="fr-sidebar-call" className="flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-3 text-sm font-bold text-slate-950 hover:bg-amber-400 transition-colors mb-3">
                <Lucide.Phone className="h-4 w-4" /> {BRAND.phone}
              </a>
              <a href={BRAND.emailHref} data-testid="fr-sidebar-email" className="flex items-center justify-center gap-2 rounded-full border border-slate-600 px-5 py-3 text-sm font-semibold text-slate-100 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
                <Lucide.Mail className="h-4 w-4" /> Ecrire a la repartition
              </a>
              <div className="mt-6 pt-5 border-t border-white/5 space-y-2.5 text-xs text-slate-400">
                <p className="flex items-center gap-2"><Lucide.Clock className="h-3.5 w-3.5 text-amber-500" /> Ouvert 24 heures, 365 jours</p>
                <p className="flex items-center gap-2"><Lucide.MapPin className="h-3.5 w-3.5 text-amber-500" /> Dessert {c.name}, Quebec</p>
                <p className="flex items-center gap-2"><Lucide.FileCheck className="h-3.5 w-3.5 text-amber-500" /> Soumissions ecrites et detaillees</p>
              </div>
            </div>
          </aside>
        </div>
        <FrLinkGrid
          testid="fr-related-services"
          title={`Autres services de securite a ${c.name}`}
          links={SERVICES.filter((x) => x.slug !== sEn.slug).map((x) => ({ path: frUrl.serviceCity(x, c), label: `${FR_SERVICES[x.slug].name} ${c.name}` }))}
        />
        <FrLinkGrid
          testid="fr-sibling-cities"
          title={`${s.name} dans d'autres villes du Quebec`}
          links={siblings.map((x) => ({ path: frUrl.serviceCity(sEn, x), label: `${s.name} ${x.name}` }))}
        />
        <FrCta heading={`Embauchez le service ${s.name.toLowerCase()} a ${c.name} aujourd'hui`} sub={`Agents licencies dans ${c.metro}. Appelez le ${BRAND.phone} ou ecrivez a ${BRAND.email} pour une soumission ecrite le jour meme.`} />
      </div>
    </div>
  );
}
